import { services } from '@config/services';
import { catalogApi } from './base';
import type { Category, Product } from './types';

/**
 * Global product-catalog read endpoints backing the Add Products selectors.
 *
 * - `getCategories` → the category tree (left rail of the selector).
 * - `getProducts` → ONE page of the GLOBAL catalog. The vendor Add Products
 *   wizard page-loops this over every page (live total ~20.8k → ~84 pages) to
 *   show the whole catalog; see `features/products/AddProducts/useAllCatalogProducts`.
 * - `getProductVariants` → the variants of one product, used to build the
 *   fillable per-product XLSX template on "Download XLSX".
 *
 * The merchant-effective catalog (getEffectiveProducts / …VariantsByProduct in
 * `./effective`) is a DIFFERENT data source used by the catalog feature's own
 * Add Products wizard. The vendor wizard reads the global catalog instead —
 * verified rank-1 from live (GET /catalog/v1/products?&page=N&pageSize=250),
 * NOT the effective endpoints — so these raw queries are re-added here.
 */

/** A money value as it appears on a global catalog variant. */
export interface CatalogVariantPrice {
  amount?: number;
  currency?: string;
  currencies?: unknown[];
}

/** A decoration area on a global catalog variant (note: the wire field for the
 * print method is `method`, NOT `printMethod`). */
export interface CatalogVariantDecoration {
  area?: string;
  width?: number;
  height?: number;
  dpi?: number;
  method?: string;
  [key: string]: unknown;
}

/** One variant of a global catalog product (Download XLSX template source). */
export interface CatalogProductVariant {
  id?: string;
  productId?: string;
  sku?: string;
  price?: CatalogVariantPrice;
  decorations?: CatalogVariantDecoration[];
  /** Legacy template-builder reads these two — they are NOT present on the
   * live payload, so the legacy emits blank cells; preserved by the port. */
  description?: string;
  productionTime?: number;
  [key: string]: unknown;
}

export interface ProductsPageResponse {
  products: Product[];
  total: number;
  /** Cursor to replay as `paginationToken` on the next request; empty/absent
   * once the walk is exhausted. */
  paginationToken?: string | null;
}

export interface ProductsPageArgs {
  page?: number;
  pageSize?: number;
  /** Opt into Atlas `searchAfter` paging; `page` is ignored server-side while
   * it is set. */
  useCursor?: boolean;
  paginationToken?: string | null;
}

export interface ProductVariantsResponse {
  variants: CatalogProductVariant[];
  total: number;
}

/** One hit of the global variant search: enough to show the match (sku) and
 * resolve it to its parent product (productId). */
export interface VariantSkuSearchHit {
  id?: string;
  sku?: string;
  productId?: string;
  [key: string]: unknown;
}

export interface VariantSkuSearchResponse {
  variants: VariantSkuSearchHit[];
  total: number;
}

export const productsApi = catalogApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /catalog/v1/categories */
    getCategories: builder.query<{ categories?: Category[] }, void>({
      query: () => ({ url: `${services.catalog}/v1/categories`, method: 'GET' }),
      providesTags: [{ type: 'Category', id: 'LIST' }],
    }),

    /**
     * GET /catalog/v1/products?&page=N&pageSize=250
     *
     * Wire-shape lock: the live URL has a literal leading `&` right after `?`
     * (the legacy builder seeded an empty query string then appended
     * `&key=value` params). Reproduced verbatim so the request matches live
     * byte-for-byte. Response envelope is `{ products, total }`, plus
     * `paginationToken` when `useCursor` is set (rank-1).
     *
     * `useCursor` + `paginationToken` opt into Atlas `searchAfter` paging,
     * which holds a flat per-page cost instead of `$skip`'s deepening re-scan;
     * see `useAllCatalogProducts` for the full-catalog walk that uses it.
     */
    getProducts: builder.query<ProductsPageResponse, ProductsPageArgs>({
      query: ({ page = 1, pageSize = 250, useCursor, paginationToken }) => {
        let url = `${services.catalog}/v1/products?&page=${page}&pageSize=${pageSize}`;
        if (useCursor) url += '&useCursor=true';
        // The service treats a blank token as an opt-in to cursor mode with an
        // invalid cursor, so only send one that actually has a value.
        if (paginationToken) url += `&paginationToken=${encodeURIComponent(paginationToken)}`;
        return { url, method: 'GET' };
      },
      transformResponse: (response: {
        products?: Product[];
        total?: number;
        paginationToken?: string | null;
      }) => ({
        products: response.products ?? [],
        total: response.total ?? 0,
        paginationToken: response.paginationToken,
      }),
      providesTags: [{ type: 'Product', id: 'LIST' }],
    }),

    /**
     * GET /catalog/v1/products/{productId}/variants?page=1&pageSize=250
     * Response envelope `{ total, variants }` (rank-1).
     */
    getProductVariants: builder.query<
      ProductVariantsResponse,
      { productId: string; page?: number; pageSize?: number }
    >({
      query: ({ productId, page = 1, pageSize = 250 }) => ({
        url: `${services.catalog}/v1/products/${productId}/variants?page=${page}&pageSize=${pageSize}`,
        method: 'GET',
      }),
      transformResponse: (response: { variants?: CatalogProductVariant[]; total?: number }) => ({
        variants: response.variants ?? [],
        total: response.total ?? 0,
      }),
      providesTags: (_r, _e, { productId }) => [{ type: 'ProductVariant', id: productId }],
    }),

    /**
     * GET /catalog/v1/variants?searchAll=<term>&page=1&pageSize=250
     *
     * Partial, case-insensitive match of `searchAll` against every GLOBAL
     * variant's SKU and variant id; each hit carries its parent productId so
     * the Add Products selectors can surface the owning product. Response
     * envelope is `{ variants, total }`.
     */
    searchVariantsBySku: builder.query<
      VariantSkuSearchResponse,
      { searchAll: string; page?: number; pageSize?: number }
    >({
      query: ({ searchAll, page = 1, pageSize = 250 }) => {
        const params = new URLSearchParams();
        params.set('searchAll', searchAll);
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
        return { url: `${services.catalog}/v1/variants?${params.toString()}`, method: 'GET' };
      },
      transformResponse: (response: { variants?: VariantSkuSearchHit[]; total?: number }) => ({
        variants: response.variants ?? [],
        total: response.total ?? 0,
      }),
      providesTags: (_r, _e, { searchAll }) => [
        { type: 'ProductVariant', id: `SKU_SEARCH:${searchAll}` },
      ],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductVariantsQuery,
  useLazyGetProductVariantsQuery,
  useSearchVariantsBySkuQuery,
} = productsApi;
